import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Save, Trash2, Plus, X, ImageOff } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

export interface BoundaryBox {
  colour: 'red' | 'blue' | 'green';
  x: number;
  y: number;
  w: number;
  h: number;
}

interface InternalBox extends BoundaryBox {
  id: string;
}

interface BoundingBoxEditorProps {
  deviceId: string;
  imageUrl: string | undefined;
  initialBoxes: BoundaryBox[];
  onSave: (boxes: BoundaryBox[]) => Promise<void>;
  disabled?: boolean;
  maskMode?: boolean;
}

// Fixed camera resolution for coordinate scaling
const TARGET_WIDTH = 2592;
const TARGET_HEIGHT = 1944;

const COLOURS: ('red' | 'blue' | 'green')[] = ['red', 'blue', 'green'];
const COLOUR_HEX: Record<string, string> = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
};

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | null;

export const BoundingBoxEditor = ({
  deviceId,
  imageUrl,
  initialBoxes,
  onSave,
  disabled = false,
  maskMode = false,
}: BoundingBoxEditorProps) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  
  const [rectangles, setRectangles] = useState<InternalBox[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState({ x: 0, y: 0 });
  const [currentDraw, setCurrentDraw] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  
  // Drag/resize state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState<ResizeHandle>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0, mouseX: 0, mouseY: 0 });

  // Track container size via ResizeObserver for accurate coordinate scaling
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setContainerSize({ width, height });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Initialize rectangles from props
  useEffect(() => {
    if (initialBoxes && Array.isArray(initialBoxes)) {
      const boxes = initialBoxes.map((box, index) => ({
        ...box,
        id: `box-${index}-${Date.now()}`,
      }));
      setRectangles(boxes);
      setHasChanges(false);
    }
  }, [initialBoxes]);

  const getNextAvailableColour = useCallback((existingRects: InternalBox[]): 'red' | 'blue' | 'green' | null => {
    const usedColours = new Set(existingRects.map(r => r.colour));
    return COLOURS.find(c => !usedColours.has(c)) || null;
  }, []);

  const handleImageLoad = () => {
    if (imageRef.current) {
      const { width, height, naturalWidth, naturalHeight } = imageRef.current;
      setImageDimensions({ width, height, naturalWidth, naturalHeight });
      setImageLoaded(true);
    }
  };

  const getScaledCoords = (clientX: number, clientY: number): { x: number; y: number } => {
    if (!containerRef.current) return { x: 0, y: 0 };
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    // Use actual current container size for scaling (not cached dimensions)
    const displayWidth = rect.width;
    const displayHeight = rect.height;
    
    // Scale to fixed target resolution (2592 x 1944)
    const scaleX = TARGET_WIDTH / displayWidth;
    const scaleY = TARGET_HEIGHT / displayHeight;
    
    return {
      x: Math.round(x * scaleX),
      y: Math.round(y * scaleY),
    };
  };

  const clampToImage = (box: { x: number; y: number; w: number; h: number }): { x: number; y: number; w: number; h: number } => {
    // Use fixed target resolution for clamping
    const maxW = TARGET_WIDTH;
    const maxH = TARGET_HEIGHT;
    
    let { x, y, w, h } = box;
    
    // Ensure positive dimensions
    if (w < 0) { x += w; w = Math.abs(w); }
    if (h < 0) { y += h; h = Math.abs(h); }
    
    // Clamp position
    x = Math.max(0, Math.min(x, maxW - w));
    y = Math.max(0, Math.min(y, maxH - h));
    
    // Clamp size
    w = Math.min(w, maxW - x);
    h = Math.min(h, maxH - y);
    
    return { x, y, w, h };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled || !imageLoaded) return;
    
    const coords = getScaledCoords(e.clientX, e.clientY);
    
    // Check if clicking on a rectangle
    const clickedRect = [...rectangles].reverse().find(rect => {
      return coords.x >= rect.x && coords.x <= rect.x + rect.w &&
             coords.y >= rect.y && coords.y <= rect.y + rect.h;
    });
    
    if (clickedRect) {
      setSelectedId(clickedRect.id);
      setIsDragging(true);
      setDragOffset({
        x: coords.x - clickedRect.x,
        y: coords.y - clickedRect.y,
      });
    } else if (rectangles.length < (maskMode ? 1 : 3)) {
      // Start drawing new rectangle
      setSelectedId(null);
      setIsDrawing(true);
      setDrawStart(coords);
      setCurrentDraw({ x: coords.x, y: coords.y, w: 0, h: 0 });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (disabled || !imageLoaded) return;
    
    const coords = getScaledCoords(e.clientX, e.clientY);
    
    if (isDrawing && currentDraw) {
      const w = coords.x - drawStart.x;
      const h = coords.y - drawStart.y;
      setCurrentDraw({
        x: w >= 0 ? drawStart.x : coords.x,
        y: h >= 0 ? drawStart.y : coords.y,
        w: Math.abs(w),
        h: Math.abs(h),
      });
    } else if (isDragging && selectedId) {
      setRectangles(prev => prev.map(rect => {
        if (rect.id !== selectedId) return rect;
        const newX = coords.x - dragOffset.x;
        const newY = coords.y - dragOffset.y;
        const clamped = clampToImage({ x: newX, y: newY, w: rect.w, h: rect.h });
        return { ...rect, x: clamped.x, y: clamped.y };
      }));
      setHasChanges(true);
    } else if (isResizing && selectedId) {
      setRectangles(prev => prev.map(rect => {
        if (rect.id !== selectedId) return rect;
        
        let newX = rect.x;
        let newY = rect.y;
        let newW = rect.w;
        let newH = rect.h;
        
        const dx = coords.x - resizeStart.mouseX;
        const dy = coords.y - resizeStart.mouseY;
        
        switch (isResizing) {
          case 'se':
            newW = Math.max(20, resizeStart.w + dx);
            newH = Math.max(20, resizeStart.h + dy);
            break;
          case 'sw':
            newX = resizeStart.x + dx;
            newW = Math.max(20, resizeStart.w - dx);
            newH = Math.max(20, resizeStart.h + dy);
            break;
          case 'ne':
            newY = resizeStart.y + dy;
            newW = Math.max(20, resizeStart.w + dx);
            newH = Math.max(20, resizeStart.h - dy);
            break;
          case 'nw':
            newX = resizeStart.x + dx;
            newY = resizeStart.y + dy;
            newW = Math.max(20, resizeStart.w - dx);
            newH = Math.max(20, resizeStart.h - dy);
            break;
        }
        
        const clamped = clampToImage({ x: newX, y: newY, w: newW, h: newH });
        return { ...rect, ...clamped };
      }));
      setHasChanges(true);
    }
  };

  const handleMouseUp = () => {
    if (isDrawing && currentDraw && currentDraw.w > 10 && currentDraw.h > 10) {
      const colour = getNextAvailableColour(rectangles);
      if (colour) {
        const clamped = clampToImage(currentDraw);
        const newRect: InternalBox = {
          id: `box-${Date.now()}`,
          colour,
          x: Math.round(clamped.x),
          y: Math.round(clamped.y),
          w: Math.round(clamped.w),
          h: Math.round(clamped.h),
        };
        setRectangles(prev => [...prev, newRect]);
        setSelectedId(newRect.id);
        setHasChanges(true);
      }
    }
    
    setIsDrawing(false);
    setCurrentDraw(null);
    setIsDragging(false);
    setIsResizing(null);
  };

  const handleResizeStart = (e: React.MouseEvent, rectId: string, handle: ResizeHandle) => {
    e.stopPropagation();
    if (disabled) return;
    
    const rect = rectangles.find(r => r.id === rectId);
    if (!rect) return;
    
    const coords = getScaledCoords(e.clientX, e.clientY);
    
    setSelectedId(rectId);
    setIsResizing(handle);
    setResizeStart({
      x: rect.x,
      y: rect.y,
      w: rect.w,
      h: rect.h,
      mouseX: coords.x,
      mouseY: coords.y,
    });
  };

  const handleDeleteSelected = () => {
    if (selectedId) {
      setRectangles(prev => prev.filter(r => r.id !== selectedId));
      setSelectedId(null);
      setHasChanges(true);
    }
  };

  const handleDeleteRect = (id: string) => {
    setRectangles(prev => prev.filter(r => r.id !== id));
    if (selectedId === id) setSelectedId(null);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const boxesToSave: BoundaryBox[] = rectangles.map(({ colour, x, y, w, h }) => ({
        colour,
        x: Math.round(x),
        y: Math.round(y),
        w: Math.round(w),
        h: Math.round(h),
      }));
      
      await onSave(boxesToSave);
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving bounding boxes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Scale display coordinates from fixed target resolution to display size
  const getDisplayCoords = (rect: { x: number; y: number; w: number; h: number }) => {
    if (containerSize.width === 0 || containerSize.height === 0) return rect;
    
    const scaleX = containerSize.width / TARGET_WIDTH;
    const scaleY = containerSize.height / TARGET_HEIGHT;
    
    return {
      x: rect.x * scaleX,
      y: rect.y * scaleY,
      w: rect.w * scaleX,
      h: rect.h * scaleY,
    };
  };

  if (!imageUrl) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
        <ImageOff className="h-12 w-12 mb-4 opacity-50" />
        <p>No image available for ROI setup</p>
        <p className="text-sm">Wait for the device to capture an image</p>
      </div>
    );
  }

  return (
    <div className={`flex gap-4 ${isMobile ? 'flex-col' : 'flex-row'}`}>
      {/* Image Canvas */}
      <div className="flex-1 min-w-0">
        <div 
          ref={containerRef}
          className="relative inline-block w-full cursor-crosshair select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {!imageLoaded && (
            <Skeleton className="w-full aspect-video" />
          )}
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Device capture"
            className="w-full h-auto rounded-lg"
            onLoad={handleImageLoad}
            style={{ display: imageLoaded ? 'block' : 'none' }}
            draggable={false}
          />
          
          {imageLoaded && (
            <svg
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              style={{ cursor: disabled ? 'default' : 'crosshair' }}
            >
              {/* Mask overlay: black out everything except the rectangle */}
              {maskMode && rectangles.length > 0 && (() => {
                const display = getDisplayCoords(rectangles[0]);
                return (
                  <>
                    <defs>
                      <mask id="mask-cutout">
                        <rect width="100%" height="100%" fill="white" />
                        <rect
                          x={display.x}
                          y={display.y}
                          width={display.w}
                          height={display.h}
                          fill="black"
                        />
                      </mask>
                    </defs>
                    <rect
                      width="100%"
                      height="100%"
                      fill="black"
                      opacity="0.7"
                      mask="url(#mask-cutout)"
                    />
                  </>
                );
              })()}

              {/* Existing rectangles */}
              {rectangles.map((rect) => {
                const display = getDisplayCoords(rect);
                const isSelected = selectedId === rect.id;
                const colour = COLOUR_HEX[rect.colour];
                
                return (
                  <g key={rect.id}>
                    <rect
                      x={display.x}
                      y={display.y}
                      width={display.w}
                      height={display.h}
                      fill={maskMode ? 'none' : `${colour}20`}
                      stroke={maskMode ? '#000000' : colour}
                      strokeWidth={isSelected ? 3 : 2}
                      className="pointer-events-auto cursor-move"
                      onClick={(e) => { e.stopPropagation(); setSelectedId(rect.id); }}
                    />
                    
                    {/* Resize handles when selected */}
                    {isSelected && !disabled && (
                      <>
                        {(['nw', 'ne', 'sw', 'se'] as ResizeHandle[]).map((handle) => {
                          let cx = display.x;
                          let cy = display.y;
                          if (handle?.includes('e')) cx = display.x + display.w;
                          if (handle?.includes('s')) cy = display.y + display.h;
                          
                          return (
                            <circle
                              key={handle}
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill={colour}
                              stroke="white"
                              strokeWidth={2}
                              className="pointer-events-auto cursor-nwse-resize"
                              onMouseDown={(e) => handleResizeStart(e, rect.id, handle)}
                            />
                          );
                        })}
                      </>
                    )}
                  </g>
                );
              })}
              
              {/* Current drawing rectangle */}
              {currentDraw && currentDraw.w > 0 && currentDraw.h > 0 && (
                <rect
                  {...getDisplayCoords(currentDraw)}
                  fill="rgba(100, 100, 100, 0.2)"
                  stroke="#666"
                  strokeWidth={2}
                  strokeDasharray="5,5"
                />
              )}
            </svg>
          )}
        </div>
        
        {rectangles.length >= (maskMode ? 1 : 3) && (
          <p className="text-sm text-muted-foreground mt-2 text-center">
            {maskMode ? 'Crop Image to Selected Area for Analysis | Delete to redraw.' : 'Maximum 3 regions allowed. Delete one to add another.'}
          </p>
        )}
      </div>
      
      {/* Controls Panel */}
      <Card className={`${isMobile ? 'w-full' : 'w-64'} shrink-0`}>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <h3 className="font-medium text-sm">{maskMode ? 'Detection Mask' : 'Regions of Interest'}</h3>
            {rectangles.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Click and drag on the image to draw a region
              </p>
            ) : (
              <div className="space-y-2">
                {rectangles.map((rect) => (
                  <div
                    key={rect.id}
                    className={`flex items-center justify-between p-2 rounded border ${
                      selectedId === rect.id ? 'border-primary bg-muted' : 'border-border'
                    }`}
                    onClick={() => setSelectedId(rect.id)}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        style={{ backgroundColor: maskMode ? '#000000' : COLOUR_HEX[rect.colour], color: 'white' }}
                        className="capitalize"
                      >
                        {maskMode ? 'Crop' : rect.colour}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {Math.round(rect.w)}×{Math.round(rect.h)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={(e) => { e.stopPropagation(); handleDeleteRect(rect.id); }}
                      disabled={disabled}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-2">
            {selectedId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteSelected}
                disabled={disabled}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            )}
            
            <Button
              onClick={handleSave}
              disabled={disabled || isSaving}
              className="w-full bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Save'}
            </Button>
          </div>
          
          {selectedId && (
            <div className="pt-2 border-t">
              <h4 className="text-xs font-medium mb-1">Selected Region</h4>
              {(() => {
                const rect = rectangles.find(r => r.id === selectedId);
                if (!rect) return null;
                return (
                  <div className="grid grid-cols-2 gap-1 text-xs text-muted-foreground font-mono">
                    <span>X: {Math.round(rect.x)}</span>
                    <span>Y: {Math.round(rect.y)}</span>
                    <span>W: {Math.round(rect.w)}</span>
                    <span>H: {Math.round(rect.h)}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BoundingBoxEditor;
